import { MAX_BATCH_ROWS, type ImportIdentityRow } from './batch-schema'

/** Canonical sample order; imports map these headers by name for v1 compatibility. */
export const ENROLLMENT_COLUMNS = [
  'email', 'last_name', 'first_name', 'username', 'address_suffix',
  'client_employee_id', 'client_department',
] as const
export const ENROLLMENT_FILE_VERSION = 2
export const MAX_ENROLLMENT_FILE_BYTES = 1024 * 1024
export type EnrollmentFileFormat = 'csv' | 'tsv'
export type EnrollmentFileRow = ImportIdentityRow & { sourceRow: number }

export function enrollmentSample(format: EnrollmentFileFormat): string {
  const separator = format === 'csv' ? ',' : '\t'
  return '\ufeff' + [
    ENROLLMENT_COLUMNS,
    ['sample.student@example.com', 'Sato', 'Yuki', '', 'san', '', ''],
  ].map(row => row.join(separator)).join('\r\n') + '\r\n'
}

/** Parse records before validating fields: quoted newlines belong to one row. */
export function parseEnrollmentFile(text: string, format: EnrollmentFileFormat): { rows: EnrollmentFileRow[] } {
  const separator = format === 'csv' ? ',' : '\t'
  const records: { cells: string[]; sourceRow: number }[] = []
  let cells: string[] = []
  let cell = ''
  let state: 'start' | 'plain' | 'quoted' | 'closed' = 'start'
  // Spreadsheet row number, including blank records and the header.
  // Physical newlines inside a quoted cell do not start another sheet row.
  let sourceRow = 1
  const invalid = () => new Error(`Malformed file at row ${sourceRow}. Correct the quotes or column count and upload again.`)
  const finishCell = () => { cells.push(cell); cell = ''; state = 'start' }
  const finishRecord = () => {
    finishCell()
    // Ignore only genuinely empty records, never a row of empty fields.
    if (cells.length !== 1 || cells[0]?.trim() !== '') records.push({ cells, sourceRow })
    cells = []
  }
  const input = text.replace(/^\ufeff/, '')
  for (let index = 0; index < input.length; index++) {
    const char = input[index]!
    if (state === 'quoted') {
      if (char === '"') {
        if (input[index + 1] === '"') { cell += '"'; index++ }
        else state = 'closed'
      } else {
        cell += char
      }
    } else if (char === separator) finishCell()
    else if (char === '\r' || char === '\n') {
      finishRecord()
      if ((char === '\r' && input[index + 1] === '\n') || (char === '\n' && input[index + 1] === '\r')) index++
      sourceRow++
    } else if (char === '"' && state === 'start') state = 'quoted'
    else {
      if (char === '"' || state === 'closed') throw invalid()
      cell += char
      state = 'plain'
    }
  }
  if (state === 'quoted') throw invalid()
  finishRecord()
  const header = records.shift()
  const columns = header?.cells.map(value => value.trim()) ?? []
  if (columns.length !== ENROLLMENT_COLUMNS.length ||
    new Set(columns).size !== columns.length ||
    ENROLLMENT_COLUMNS.some(column => !columns.includes(column))) {
    throw new Error(`Expected these headers (downloaded samples use this order): ${ENROLLMENT_COLUMNS.join(',')}. Download the current sample and update your file.`)
  }
  if (records.length === 0) throw new Error('Add at least one student below the header.')
  if (records.length > MAX_BATCH_ROWS) throw new Error(`A batch is at most ${MAX_BATCH_ROWS} students. Split the file and upload again.`)
  return { rows: records.map(record => {
    sourceRow = record.sourceRow
    if (record.cells.length !== ENROLLMENT_COLUMNS.length) throw invalid()
    // Map by header so legacy files cannot silently swap family and given names.
    const cellFor = (column: typeof ENROLLMENT_COLUMNS[number]) => record.cells[columns.indexOf(column)]?.trim() ?? ''
    const email = cellFor('email')
    const firstName = cellFor('first_name')
    const lastName = cellFor('last_name')
    const username = cellFor('username')
    const suffix = cellFor('address_suffix')
    const clientEmployeeId = cellFor('client_employee_id')
    const clientDepartment = cellFor('client_department')
    return {
      sourceRow: record.sourceRow, email, firstName, lastName, username,
      addressSuffix: (suffix || 'none') as ImportIdentityRow['addressSuffix'],
      clientEmployeeId, clientDepartment,
      ...(suffix === 'none' ? { parseErrorField: 'addressSuffix' as const, parseError: 'For no coach suffix, leave the suffix cell blank instead of entering none.' } : {}),
    }
  }) }
}
